import { ChangeDetectionStrategy, Component, effect, inject, TemplateRef, untracked, viewChild } from '@angular/core';

import { ButtonModule, DIALOG_DATA, DialogRef, IconModule, OkDialog } from '@hglobal/fin-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { WalletCardComponent } from '@kr/components';
import { environment } from '@kr/environment';
import { Coin, Transfer, WalletCard } from '@kr/models';
import { CurrencyBalanceFormatPipe } from '@kr/pipes';
import { ClipboardService } from '@kr/services';

import { WarningSaveBalanceComponent } from '../../../warning-save-balance/warning-save-balance.component';
import { TRANSFER_API } from '../../api/transfer-api.interface';
import { TransferApiService } from '../../api/transfer-api.service';
import { TransferApiMockService } from '../../mocks/transfer-api-mock.service';
import { TransferStore } from '../../transfer.store';

@Component({
  imports: [
    TranslateModule,
    IconModule,
    ButtonModule,
    WalletCardComponent,
    // NgClass,
    // CopyTooltipComponent,
    // RiskScoreValueComponent,
    WarningSaveBalanceComponent,
    CurrencyBalanceFormatPipe,
  ],
  providers: [
    TransferStore,
    {
      provide: TRANSFER_API,
      useClass: environment.useMocks ? TransferApiMockService : TransferApiService,
    },
  ],
  selector: 'kr-transfer-details',
  templateUrl: './transfer-details.component.html',
  styleUrls: ['./transfer-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransferDetailsComponent {
  readonly #translate = inject(TranslateService);
  readonly #dialogRef = inject(DialogRef);
  readonly #okDialog = inject(OkDialog);
  readonly #store = inject(TransferStore);
  readonly clipboard = inject(ClipboardService);
  readonly data = inject<{ transfer: Transfer; walletFrom: WalletCard; walletTo?: WalletCard }>(DIALOG_DATA);

  readonly successTpl = viewChild<TemplateRef<any>>('successTpl');
  readonly failedTpl = viewChild<TemplateRef<any>>('failedTpl');

  transfer: Transfer;
  coin: Coin;
  walletFrom: WalletCard;
  walletTo?: WalletCard;
  errMessage: string;
  isExecuting = false;

  response = this.#store.payOutRes;

  readonly transferSucceed = effect(() => {
    const isInternalSuccess = this.#store.isExecuteTransferInternalSuccess();
    const isExternalSuccess = this.#store.isExecuteTransferExternalSuccess();
    if (isInternalSuccess || isExternalSuccess) {
      untracked(() => {
        this.openSuccessDialog();
        this.isExecuting = false;
      });
    }
  });

  readonly transferFailed = effect(() => {
    const internalError = this.#store.executeTransferInternalError();
    const externalError = this.#store.executeTransferExternalError();
    if (internalError || externalError) {
      untracked(() => {
        this.errMessage = (internalError || externalError)?.originalError.error.error_description || 'Unknown error';
        this.openFailedDialog();
        this.isExecuting = false;
      });
    }
  });

  get header() {
    return this.walletFrom.name + '/' + this.#translate.instant('transfer.title');
  }

  constructor() {
    this.transfer = this.data.transfer;
    this.walletFrom = this.data.walletFrom;
    this.walletTo = this.data.walletTo;
    this.coin = this.transfer.coinId === this.walletFrom.coinId ? this.walletFrom.coin : this.walletFrom.secondaryWalletCoin;
  }

  execute(): void {
    if (this.isExecuting) return;
    this.isExecuting = true;

    const orgId = this.walletFrom.organizationId;
    this.walletTo
      ? this.#store.executeTransferInternal({ body: this.transfer, orgId })
      : this.#store.executeTransferExternal({ body: this.transfer, orgId });
  }

  openSuccessDialog(): void {
    this.#okDialog
      .open({
        headerKey: this.header,
        contentTpl: this.successTpl(),
        noOk: true,
        noCancel: true,
      })
      .afterClosed()
      .subscribe(() => this.#dialogRef.close(true));
  }

  openFailedDialog(): void {
    this.#okDialog
      .open({
        headerKey: this.header,
        contentTpl: this.failedTpl(),
        noOk: true,
        noCancel: true,
      })
      .afterClosed()
      .subscribe(() => this.#dialogRef.close(false));
  }

  close(): void {
    this.#dialogRef.close();
  }
}
